import type {
  AgentPerfil, AgentPerfilAplicado, AgentPerfilCampos, AgentPerfilValor,
} from '../../api/types';

/**
 * THE PROFILE EDITOR LOGIC, separated from the paint so it can be tested on its own.
 *
 * The SEVEN authored fields of the alias (`agent_profiles`, migration 026) end up, word for word, in the
 * files READ by that alias's harness. Which file receives each field is NOT fixed: it is decided by
 * `ficherosDelArnes` in `@cauce/protocol` according to the harness, and `destinosDelArnes` reproduces that
 * distribution here so it can be labeled. `perfil.test.ts` ties the table to the real composition function,
 * which is what keeps them from drifting apart.
 *
 * Permissions, quotas, harness, and reachable aliases are NOT fields: they are FACTS read fresh from
 * `memberships`, `role_policies`, `provider_accounts`, and `agents`. They appear in the preview because they
 * go into the file, but they have no input box. Copying them as authored text would be a second source of
 * truth that silently desynchronizes: a permission gets revoked and the container's file keeps claiming it.
 *
 * The unit count is measured with `max(code points, UTF-16 units)`, the same thing the Postgres CHECK and
 * the compiler measure.
 */

/** Free-text fields, in the order they are painted. */
export const CAMPOS_DE_TEXTO = ['purpose', 'role_summary', 'human_brief'] as const;

/** List fields, in the order they are painted. */
export const CAMPOS_DE_LISTA = ['responsibilities', 'restrictions', 'tools', 'operating_rules'] as const;

type CampoDeTexto = (typeof CAMPOS_DE_TEXTO)[number];
type CampoDeLista = (typeof CAMPOS_DE_LISTA)[number];
export type CampoDelPerfil = CampoDeTexto | CampoDeLista;

export const CAMPOS_DEL_PERFIL: readonly CampoDelPerfil[] = [
  ...CAMPOS_DE_TEXTO, ...CAMPOS_DE_LISTA,
];

/**
 * How each field is named on screen, and what the operator is expected to put there. The help is not
 * decoration: the column names are in English because the schema is, and `operating_rules` does not tell
 * anyone what to write. Without this, the operator fills `purpose` with what should go in `role_summary`, and
 * openclaw's `SOUL.md` ends up talking about tasks — which is exactly how you teach a model that its identity
 * is its tasks.
 */
export const ETIQUETAS: Record<CampoDelPerfil, { titulo: string; ayuda: string }> = {
  purpose: {
    titulo: 'Identidad y propósito',
    ayuda: 'Quién es este alias y para qué existe. No sus tareas: su razón de ser.',
  },
  role_summary: {
    titulo: 'Rol declarado',
    ayuda: 'Qué papel ocupa en la flota. Sucede al viejo role_brief, con sitio para el detalle que allá no cabía.',
  },
  human_brief: {
    titulo: 'Tu humano y cómo tratarlo',
    ayuda: 'Quién es la persona con la que trata y cómo quiere que le hablen.',
  },
  responsibilities: {
    titulo: 'Responsabilidades',
    ayuda: 'Qué le toca hacer. Una por línea.',
  },
  restrictions: {
    titulo: 'Restricciones',
    ayuda: 'Qué NO puede hacer, aunque pudiera. Una por línea.',
  },
  tools: {
    titulo: 'Herramientas declaradas en el contexto',
    ayuda: 'Describe con qué cuenta; no concede permisos ni capacidades. Una por línea.',
  },
  operating_rules: {
    titulo: 'Instrucciones fijas de funcionamiento',
    ayuda: 'Cómo se trabaja aquí. Lo que hoy se reinyecta en cada mensaje y debería estar escrito una sola vez.',
  },
};

/** Where a field ends up: a named file, or an absence with the word it deserves. */
export type DestinoDelCampo =
  | { readonly tipo: 'fichero'; readonly nombre: string }
  | { readonly tipo: 'ausente'; readonly ausente: 'sin-dato' | 'no-aplica'; readonly motivo: string };

/** The only harness that splits the fields across several files; claude and codex merge them into one. */
const REPARTO_OPENCLAW: Record<CampoDelPerfil, string> = {
  purpose: 'SOUL.md',
  role_summary: 'IDENTITY.md',
  human_brief: 'USER.md',
  responsibilities: 'AGENTS.md',
  restrictions: 'AGENTS.md',
  tools: 'TOOLS.md',
  operating_rules: 'AGENTS.md',
};

function ficheroDelCampo(harness: string, campo: CampoDelPerfil): string | undefined {
  if (harness === 'claude') return 'CLAUDE.md';
  if (harness === 'codex') return 'AGENTS.md';
  if (harness === 'muse' || harness === 'grok') return 'AGENTS.md';
  if (harness === 'openclaw') return REPARTO_OPENCLAW[campo];
  return undefined;
}

/**
 * Which file each field goes to IN THIS alias, with the harness the server declares. A destination is only
 * asserted if the harness is one Cauce knows how to compose AND the gateway publishes that file among its own:
 * labeling `SOUL.md` over a response that does not carry it would be promising a write nobody will attest. A
 * harness that is not claude, codex, or openclaw does not receive ANY file —`nombresDelArnes` returns empty—,
 * and that is stated, not substituted with openclaw.
 */
export function destinosDelArnes(
  harness: string | null | undefined,
  ficheros: readonly { nombre: string }[] | undefined,
): Record<CampoDelPerfil, DestinoDelCampo> {
  const arnes = typeof harness === 'string' ? harness.trim() : '';
  const publicados = new Set((ficheros ?? []).map((fichero) => fichero.nombre));
  const destinos = {} as Record<CampoDelPerfil, DestinoDelCampo>;
  for (const campo of CAMPOS_DEL_PERFIL) {
    const nombre = arnes.length === 0 ? undefined : ficheroDelCampo(arnes, campo);
    destinos[campo] = nombre !== undefined && publicados.has(nombre)
      ? { tipo: 'fichero', nombre }
      : { tipo: 'ausente', ...ausenciaDeDestino(arnes, nombre) };
  }
  return destinos;
}

function ausenciaDeDestino(
  arnes: string,
  nombre: string | undefined,
): { ausente: 'sin-dato' | 'no-aplica'; motivo: string } {
  if (arnes.length === 0) {
    return {
      ausente: 'sin-dato',
      motivo: 'El registro no dice qué arnés corre este alias, así que no se puede saber qué '
        + 'fichero lee.',
    };
  }
  if (nombre === undefined) {
    return {
      ausente: 'no-aplica',
      motivo: `El arnés «${arnes}» no publica una proyección canónica editable en este servidor. `
        + 'Esto no significa que no pueda ejecutar tareas o conversar.',
    };
  }
  return {
    ausente: 'sin-dato',
    motivo: `El arnés «${arnes}» escribiría ${nombre}, pero este servidor no lo publica entre sus `
      + 'ficheros gobernados, así que no se promete esa escritura.',
  };
}

/** The common reason when NO field has a destination; `undefined` as soon as one does. */
export function motivoSinDestino(
  destinos: Record<CampoDelPerfil, DestinoDelCampo>,
): string | undefined {
  const motivos = new Set<string>();
  for (const campo of CAMPOS_DEL_PERFIL) {
    const destino = destinos[campo];
    if (destino.tipo === 'fichero') return undefined;
    motivos.add(destino.motivo);
  }
  return [...motivos].join(' ');
}

/**
 * Match `measureStrictestUnits` using the larger UTF-16/code-point count without importing
 * the protocol's Node barrel into the browser bundle.
 */
export function contarUnidades(texto: string): number {
  return Math.max(Array.from(texto).length, texto.length);
}

/** What the operator has in front of them: the saved profile with the draft on top if there is one. */
export function camposVigentes(
  perfil: AgentPerfil | undefined,
  borrador: Partial<AgentPerfilCampos> | undefined,
): AgentPerfilCampos {
  const base: AgentPerfilCampos = {
    purpose: perfil?.perfil.purpose ?? '',
    role_summary: perfil?.perfil.role_summary ?? '',
    human_brief: perfil?.perfil.human_brief ?? '',
    responsibilities: [...(perfil?.perfil.responsibilities ?? [])],
    restrictions: [...(perfil?.perfil.restrictions ?? [])],
    tools: [...(perfil?.perfil.tools ?? [])],
    operating_rules: [...(perfil?.perfil.operating_rules ?? [])],
  };
  if (borrador === undefined) return base;
  return { ...base, ...borrador };
}

/** One entry per line, kept AS TYPED: normalising each keystroke ate the space and the newline. */
export function lineasCrudas(texto: string): string[] {
  return texto.split('\n');
}

/** The entries that count: trimmed, no blank lines. Applied when measuring and when saving. */
export function entradasDeLista(items: readonly string[]): string[] {
  return items.map((linea) => linea.trim()).filter((linea) => linea.length > 0);
}

export function listaALineas(items: readonly string[]): string {
  return items.join('\n');
}

/** Did anything change from what is saved? Compares value by value, not by object identity. */
export function hayCambios(
  perfil: AgentPerfil | undefined,
  campos: AgentPerfilCampos,
): boolean {
  const guardado = camposVigentes(perfil, undefined);
  for (const campo of CAMPOS_DE_TEXTO) {
    if (campos[campo] !== guardado[campo]) return true;
  }
  for (const campo of CAMPOS_DE_LISTA) {
    const a = entradasDeLista(campos[campo]);
    const b = entradasDeLista(guardado[campo]);
    if (a.length !== b.length) return true;
    if (a.some((item, i) => item !== b[i])) return true;
  }
  return false;
}

/**
 * What the whole profile measures, by the same criterion as migration 026's ceiling: it sums the texts and
 * EVERY entry of every list. A field within its own ceiling says nothing about the total: four full lists give
 * 256,000 units with every field "within its own".
 */
export function unidadesDelPerfil(campos: AgentPerfilCampos): number {
  let total = 0;
  for (const campo of CAMPOS_DE_TEXTO) total += contarUnidades(campos[campo]);
  for (const campo of CAMPOS_DE_LISTA) {
    for (const item of entradasDeLista(campos[campo])) total += contarUnidades(item);
  }
  return total;
}

/** Which fields exceed their ceiling, with the measured number. Empty = everything fits. */
export function camposQueNoEntran(
  campos: AgentPerfilCampos,
  limites: AgentPerfil['limites'] | undefined,
): { campo: string; medido: number; tope: number }[] {
  if (limites === undefined) return [];
  const fuera: { campo: string; medido: number; tope: number }[] = [];

  for (const campo of CAMPOS_DE_TEXTO) {
    const tope = campo === 'role_summary' ? limites.role_summary : limites.purpose;
    const medido = contarUnidades(campos[campo]);
    if (medido > tope) fuera.push({ campo: ETIQUETAS[campo].titulo, medido, tope });
  }

  for (const campo of CAMPOS_DE_LISTA) {
    const items = entradasDeLista(campos[campo]);
    if (items.length > limites.items) {
      fuera.push({ campo: `${ETIQUETAS[campo].titulo} (nº de entradas)`, medido: items.length, tope: limites.items });
    }
    for (const item of items) {
      const medido = contarUnidades(item);
      if (medido > limites.item) {
        fuera.push({ campo: `${ETIQUETAS[campo].titulo} (una entrada)`, medido, tope: limites.item });
        break;
      }
    }
  }

  const total = unidadesDelPerfil(campos);
  if (total > limites.total) fuera.push({ campo: 'El perfil entero', medido: total, tope: limites.total });
  return fuera;
}

/** Canonical body of the applied route; it does not contain client-controlled identity or action. */
export function perfilParaGuardar(campos: AgentPerfilCampos): AgentPerfilValor {
  const texto = (valor: string): string | null => (valor.trim().length === 0 ? null : valor);
  return {
    purpose: texto(campos.purpose),
    role_summary: texto(campos.role_summary),
    human_brief: texto(campos.human_brief),
    responsibilities: entradasDeLista(campos.responsibilities),
    restrictions: entradasDeLista(campos.restrictions),
    tools: entradasDeLista(campos.tools),
    operating_rules: entradasDeLista(campos.operating_rules),
  };
}

const SHA256 = /^[0-9a-f]{64}$/;

/**
 * A 2xx only attests application if the same revision converges and carries exactly one valid ACK per
 * expected file. It is validated at runtime because the remote JSON does not know the TS types.
 */
export function esPerfilAplicado(
  value: unknown,
  esperado: { tenantId: string; alias: string; nombres: readonly string[] },
): value is AgentPerfilAplicado {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (record.ok !== true || record.state !== 'applied'
    || record.tenant_id !== esperado.tenantId || record.alias !== esperado.alias
    || typeof record.revision !== 'number' || !Number.isSafeInteger(record.revision)
    || record.revision <= 0 || record.applied_revision !== record.revision
    || !Array.isArray(record.acknowledgements) || esperado.nombres.length === 0
    || record.acknowledgements.length !== esperado.nombres.length) return false;

  const pendientes = new Set(esperado.nombres);
  if (pendientes.size !== esperado.nombres.length) return false;
  let generation: string | undefined;
  const ackPorNombre = new Map<string, { path: string; sha: string }>();
  for (const rawAck of record.acknowledgements) {
    if (rawAck === null || typeof rawAck !== 'object' || Array.isArray(rawAck)) return false;
    const ack = rawAck as Record<string, unknown>;
    if (typeof ack.name !== 'string' || !pendientes.delete(ack.name)
      || typeof ack.path !== 'string' || !ack.path.startsWith('/')
      || !ack.path.endsWith(`/${ack.name}`)
      || !['written', 'already_current', 'preserved'].includes(String(ack.state))
      || typeof ack.sha !== 'string' || !SHA256.test(ack.sha)
      || typeof ack.bytes !== 'number' || !Number.isSafeInteger(ack.bytes) || ack.bytes < 0
      || typeof ack.generation !== 'string' || ack.generation.length === 0
      || (ack.container_id !== null
        && (typeof ack.container_id !== 'string' || ack.container_id.length === 0))) {
      return false;
    }
    generation ??= ack.generation;
    if (ack.generation !== generation) return false;
    ackPorNombre.set(ack.name, { path: ack.path, sha: ack.sha });
  }
  if (pendientes.size !== 0 || generation === undefined
    || record.runtime_adoption === null || typeof record.runtime_adoption !== 'object'
    || Array.isArray(record.runtime_adoption)) return false;
  const adoption = record.runtime_adoption as Record<string, unknown>;
  if (adoption.evidence !== 'adapter_delivery' || adoption.revision !== record.revision
    || adoption.generation !== generation || typeof adoption.adopted_at !== 'string'
    || !Number.isFinite(Date.parse(adoption.adopted_at))
    || !Array.isArray(adoption.documents)
    || adoption.documents.length !== ackPorNombre.size) return false;
  const adoptados = new Set(ackPorNombre.keys());
  for (const rawDocument of adoption.documents) {
    if (rawDocument === null || typeof rawDocument !== 'object' || Array.isArray(rawDocument)) {
      return false;
    }
    const document = rawDocument as Record<string, unknown>;
    if (typeof document.name !== 'string' || !adoptados.delete(document.name)) return false;
    const ack = ackPorNombre.get(document.name);
    if (ack === undefined || document.path !== ack.path || document.sha !== ack.sha) return false;
  }
  return adoptados.size === 0;
}

export const ESTADOS_DE_APLICACION = [
  'absent', 'disabled', 'pending', 'written_pending_session', 'pending_session_refresh',
  'runtime_unverified', 'drifted', 'applied',
] as const;

type EstadoDeAplicacion = (typeof ESTADOS_DE_APLICACION)[number];

export const MENSAJES_DE_APLICACION: Readonly<Record<EstadoDeAplicacion, string>> = {
  absent: 'no hay contexto guardado todavía para este alias.',
  disabled: 'el alias está apagado: su contexto no se aplica mientras está apagado.',
  pending: 'hay una revisión guardada que todavía no llegó a los ficheros del contenedor.',
  written_pending_session: 'el fichero quedó escrito y la sonda acreditó los bytes, pero un ACK de '
    + 'escritura no prueba que el proceso releyera el fichero: se aplicará cuando la sesión '
    + 'recargue su contexto.',
  pending_session_refresh: 'el contexto está en disco y verificado, pero la sesión compartida '
    + 'todavía no acreditó haberlo adoptado.',
  runtime_unverified: 'no se pudo verificar el entorno del agente, así que no se afirma nada sobre lo que el '
    + 'proceso lee.',
  drifted: 'lo que hay en el contenedor no coincide con la revisión guardada.',
  applied: 'la sesión acreditó con su propio ACK haber adoptado esta revisión.',
};

export const MOTIVOS_DE_CONTAMINACION: Readonly<Record<string, string>> = {
  foreign_managed_block: 'bloque gestionado de otro alias',
  expectation_sha_mismatch: 'huella distinta de la esperada',
};

export function fraseDeContaminacion(motivo: string): string {
  return MOTIVOS_DE_CONTAMINACION[motivo]
    ?? 'algo que esta consola todavía no sabe nombrar; míralo dentro del contenedor';
}

interface HallazgoDeContaminacion {
  readonly reason: string;
  readonly document: string;
  readonly path: string;
  readonly owner?: string;
  readonly expected_sha?: string;
  readonly observed_sha?: string | null;
}

export interface ContaminacionDeContexto {
  readonly contaminated: boolean;
  readonly findings: readonly HallazgoDeContaminacion[];
}

export const CONTAMINACION_ILEGIBLE: ContaminacionDeContexto = { contaminated: true, findings: [] };

function hallazgoLeido(value: unknown): HallazgoDeContaminacion | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const row = value as Record<string, unknown>;
  if (typeof row.reason !== 'string' || row.reason.length === 0
    || typeof row.document !== 'string' || typeof row.path !== 'string'
    || (row.owner !== undefined && typeof row.owner !== 'string')) return undefined;
  return {
    reason: row.reason,
    document: row.document,
    path: row.path,
    ...(typeof row.owner === 'string' ? { owner: row.owner } : {}),
    ...(typeof row.expected_sha === 'string' ? { expected_sha: row.expected_sha } : {}),
    ...(typeof row.observed_sha === 'string' || row.observed_sha === null
      ? { observed_sha: row.observed_sha }
      : {}),
  };
}

export function contaminacionDe(respuesta: unknown): ContaminacionDeContexto | undefined {
  if (respuesta === null || typeof respuesta !== 'object' || Array.isArray(respuesta)) {
    return undefined;
  }
  const bruto = (respuesta as Record<string, unknown>).contaminacion;
  if (bruto === undefined) return undefined;
  if (bruto === null || typeof bruto !== 'object' || Array.isArray(bruto)) {
    return CONTAMINACION_ILEGIBLE;
  }
  const row = bruto as Record<string, unknown>;
  if (typeof row.contaminated !== 'boolean' || !Array.isArray(row.findings)) {
    return CONTAMINACION_ILEGIBLE;
  }
  const findings: HallazgoDeContaminacion[] = [];
  for (const item of row.findings) {
    const leido = hallazgoLeido(item);
    if (leido === undefined) return CONTAMINACION_ILEGIBLE;
    findings.push(leido);
  }
  if (row.contaminated !== (findings.length > 0)) return CONTAMINACION_ILEGIBLE;
  return { contaminated: row.contaminated, findings };
}

/** The verdict a response STATES, only when it can be read whole: an illegible one is not a verdict. */
export function veredictoLegible(respuesta: unknown): ContaminacionDeContexto | undefined {
  const leido = contaminacionDe(respuesta);
  return leido === undefined || leido === CONTAMINACION_ILEGIBLE ? undefined : leido;
}

/** Of two measurements the one that DENOUNCES rules: a clean verdict never buries a dirty one. */
export function veredictoVigente(
  deLaRespuesta: ContaminacionDeContexto | undefined,
  deLaLectura: ContaminacionDeContexto | undefined,
): ContaminacionDeContexto | undefined {
  if (deLaRespuesta?.contaminated === true) return deLaRespuesta;
  if (deLaLectura?.contaminated === true) return deLaLectura;
  return deLaRespuesta ?? deLaLectura;
}

export function entregasEnVuelo(cuerpo: unknown): string[] {
  if (cuerpo === null || typeof cuerpo !== 'object' || Array.isArray(cuerpo)) return [];
  const bruto = (cuerpo as Record<string, unknown>).deliveries;
  if (!Array.isArray(bruto)) return [];
  const nombradas: string[] = [];
  for (const item of bruto) {
    if (typeof item === 'string') {
      if (item.trim().length > 0) nombradas.push(item.trim());
      continue;
    }
    if (item === null || typeof item !== 'object' || Array.isArray(item)) continue;
    const fila = item as Record<string, unknown>;
    const id = [fila.delivery_id, fila.message_id, fila.id]
      .find((valor) => typeof valor === 'string' && valor.trim().length > 0);
    if (typeof id === 'string') nombradas.push(id.trim());
  }
  return nombradas;
}

interface DocumentoRecargado {
  readonly name: string;
  readonly path: string;
  readonly sha_before: string | null;
  readonly sha_after: string;
  readonly bytes: number;
}

export interface RespuestaDeRecarga {
  readonly ok: true;
  readonly state: EstadoDeAplicacion;
  readonly evidence: string;
  readonly message: string;
  readonly tenant_id: string;
  readonly alias: string;
  readonly revision: number;
  readonly runtime_verification: NonNullable<AgentPerfil['runtime_verification']>;
  readonly documents: readonly DocumentoRecargado[];
  readonly contaminacion: ContaminacionDeContexto;
}

function esEstadoDeAplicacion(value: unknown): value is EstadoDeAplicacion {
  return typeof value === 'string'
    && (ESTADOS_DE_APLICACION as readonly string[]).includes(value);
}

function esDocumentoRecargado(value: unknown): boolean {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return typeof row.name === 'string' && row.name.length > 0
    && typeof row.path === 'string' && row.path.startsWith('/')
    && (row.sha_before === null
      || (typeof row.sha_before === 'string' && SHA256.test(row.sha_before)))
    && typeof row.sha_after === 'string' && SHA256.test(row.sha_after)
    && typeof row.bytes === 'number' && Number.isSafeInteger(row.bytes) && row.bytes >= 0;
}

function esVerificacionDeRuntime(value: unknown): boolean {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return typeof row.state === 'string'
    && (row.generation === null || typeof row.generation === 'string')
    && Array.isArray(row.documents);
}

export function esRecargaHecha(
  value: unknown,
  esperado: { tenantId: string; alias: string },
): value is RespuestaDeRecarga {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return row.ok === true && esEstadoDeAplicacion(row.state)
    && typeof row.evidence === 'string' && row.evidence.length > 0
    && typeof row.message === 'string' && row.message.length > 0
    && row.tenant_id === esperado.tenantId && row.alias === esperado.alias
    && typeof row.revision === 'number' && Number.isSafeInteger(row.revision) && row.revision > 0
    && esVerificacionDeRuntime(row.runtime_verification)
    && Array.isArray(row.documents) && row.documents.every(esDocumentoRecargado)
    && veredictoLegible(row) !== undefined;
}

export interface PerfilRevision {
  readonly id: string;
  readonly tenant_id: string;
  readonly alias: string;
  readonly revision: number;
  readonly operation: 'insert' | 'update' | 'delete';
  readonly purpose: string | null;
  readonly role_summary: string | null;
  readonly human_brief: string | null;
  readonly responsibilities: readonly string[];
  readonly restrictions: readonly string[];
  readonly tools: readonly string[];
  readonly operating_rules: readonly string[];
  readonly actor_tenant: string | null;
  readonly actor_alias: string | null;
  readonly changed_at: string;
}

export interface DocumentoRevision {
  readonly id: string;
  readonly tenant_id: string;
  readonly alias: string;
  readonly kind: string;
  readonly path: string;
  readonly sha256: string | null;
  readonly bytes: number;
  readonly actor_tenant: string | null;
  readonly actor_alias: string | null;
  readonly written_at: string;
}

export interface PaginaDeRevisiones<T> {
  readonly observed_at: string;
  readonly tenant_id: string;
  readonly alias: string;
  readonly entries: readonly T[];
}

export interface PaginaDeRevisionesDeDocumento extends PaginaDeRevisiones<DocumentoRevision> {
  readonly kind: string;
}

export interface TramoDeRevisiones {
  readonly limit?: number;
  readonly cursor?: string;
}
